import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { philosophyApi, thesisApi, ruleApi, holdingThesisApi, seedExamplesApi, allocationsApi, healthScoresApi } from '../api/thesis'
import type {
  PhilosophyCreate,
  PhilosophyUpdate,
  ThesisCreate,
  ThesisUpdate,
  RuleCreate,
  RuleUpdate,
  HoldingThesisLink,
} from '../types/thesis'

// Query keys
const PHILOSOPHIES_KEY = ['philosophies']
const THESES_KEY = ['theses']
const RULES_KEY = ['rules']

// ===== PHILOSOPHY HOOKS =====

export function usePhilosophies() {
  return useQuery({
    queryKey: PHILOSOPHIES_KEY,
    queryFn: philosophyApi.list,
  })
}

export function usePhilosophy(id: string) {
  return useQuery({
    queryKey: [...PHILOSOPHIES_KEY, id],
    queryFn: () => philosophyApi.get(id),
    enabled: !!id,
  })
}

export function useCreatePhilosophy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PhilosophyCreate) => philosophyApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PHILOSOPHIES_KEY })
    },
  })
}

export function useUpdatePhilosophy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PhilosophyUpdate }) =>
      philosophyApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PHILOSOPHIES_KEY })
    },
  })
}

export function useDeletePhilosophy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => philosophyApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PHILOSOPHIES_KEY })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
    },
  })
}

// ===== THESIS HOOKS =====

export function useTheses(params?: { philosophy_id?: string; status?: string }) {
  return useQuery({
    queryKey: [...THESES_KEY, params],
    queryFn: () => thesisApi.list(params),
  })
}

export function useThesis(id: string) {
  return useQuery({
    queryKey: [...THESES_KEY, id],
    queryFn: () => thesisApi.get(id),
    enabled: !!id,
  })
}

export function useCreateThesis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ThesisCreate) => thesisApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
      queryClient.invalidateQueries({ queryKey: PHILOSOPHIES_KEY })
    },
  })
}

export function useUpdateThesis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ThesisUpdate }) => thesisApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
    },
  })
}

export function useDeleteThesis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => thesisApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
      queryClient.invalidateQueries({ queryKey: PHILOSOPHIES_KEY })
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
    },
  })
}

export function useSuggestRules() {
  return useMutation({
    mutationFn: (thesisId: string) => thesisApi.suggestRules(thesisId),
  })
}

// ===== RULE HOOKS =====

export function useRules(params?: {
  thesis_id?: string
  enabled?: boolean
  condition_type?: string
}) {
  return useQuery({
    queryKey: [...RULES_KEY, params],
    queryFn: () => ruleApi.list(params),
  })
}

export function useRule(id: string) {
  return useQuery({
    queryKey: [...RULES_KEY, id],
    queryFn: () => ruleApi.get(id),
    enabled: !!id,
  })
}

export function useCreateRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RuleCreate) => ruleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
    },
  })
}

export function useUpdateRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RuleUpdate }) => ruleApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
    },
  })
}

export function useDeleteRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => ruleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
    },
  })
}

// Quick toggle for rule enable/disable
export function useToggleRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      ruleApi.update(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
    },
  })
}

// ===== HOLDING-THESIS LINKING HOOKS =====

export function useHoldingTheses(holdingId: string) {
  return useQuery({
    queryKey: ['holdingTheses', holdingId],
    queryFn: () => holdingThesisApi.getHoldingTheses(holdingId),
    enabled: !!holdingId,
  })
}

export function useSetHoldingTheses() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ holdingId, data }: { holdingId: string; data: HoldingThesisLink }) =>
      holdingThesisApi.setHoldingTheses(holdingId, data),
    onSuccess: (_, { holdingId }) => {
      queryClient.invalidateQueries({ queryKey: ['holdingTheses', holdingId] })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
    },
  })
}

export function useLinkHoldingToThesis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ holdingId, thesisId }: { holdingId: string; thesisId: string }) =>
      holdingThesisApi.linkHoldingToThesis(holdingId, thesisId),
    onSuccess: (_, { holdingId }) => {
      queryClient.invalidateQueries({ queryKey: ['holdingTheses', holdingId] })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
    },
  })
}

export function useUnlinkHoldingFromThesis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ holdingId, thesisId }: { holdingId: string; thesisId: string }) =>
      holdingThesisApi.unlinkHoldingFromThesis(holdingId, thesisId),
    onSuccess: (_, { holdingId }) => {
      queryClient.invalidateQueries({ queryKey: ['holdingTheses', holdingId] })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
    },
  })
}

// ===== THESIS ALLOCATIONS =====

const ALLOCATIONS_KEY = ['thesisAllocations']

export function useThesisAllocations(portfolio_id?: string | null) {
  return useQuery({
    queryKey: [...ALLOCATIONS_KEY, portfolio_id],
    queryFn: () => allocationsApi.get(portfolio_id),
  })
}

// ===== THESIS HEALTH SCORES =====

const HEALTH_SCORES_KEY = ['thesisHealthScores']

export function useThesisHealthScores(portfolio_id?: string | null) {
  return useQuery({
    queryKey: [...HEALTH_SCORES_KEY, portfolio_id],
    queryFn: () => healthScoresApi.get(portfolio_id),
  })
}

// ===== SEED EXAMPLES =====

export function useSeedExamples() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => seedExamplesApi.seed(),
    onSuccess: () => {
      // Invalidate all thesis-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: PHILOSOPHIES_KEY })
      queryClient.invalidateQueries({ queryKey: THESES_KEY })
      queryClient.invalidateQueries({ queryKey: RULES_KEY })
      queryClient.invalidateQueries({ queryKey: ALLOCATIONS_KEY })
    },
  })
}
